import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog'
import { Label } from './ui/label'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { apiSendReminders, DashboardSupplier } from '@/lib/api'

type Props = {
  campaignId: string
  campaignName: string
  suppliers: DashboardSupplier[]
  memberName: string
  memberEmail: string
  onSuccess: () => void
  onClose: () => void
}

function getDefaultMessage(suppliers: DashboardSupplier[]): string {
  // Message pré-rédigé adapté au statut (UX-11)
  const hasNotOpened = suppliers.some(s => s.uxStatus === 'not_opened')
  const hasInProgress = suppliers.some(s => s.uxStatus === 'in_progress' || s.uxStatus === 'opened')
  if (hasNotOpened && !hasInProgress) {
    return `Nous espérons que vous allez bien. Nous souhaitions vous rappeler que votre formulaire de collecte de données environnementales vous attend. N'hésitez pas à nous contacter si vous avez des questions.`
  }
  if (hasInProgress && !hasNotOpened) {
    return `Nous avons remarqué que vous avez commencé à remplir le formulaire. Merci de finaliser la saisie dans les meilleurs délais pour nous permettre de respecter nos délais d'analyse.`
  }
  return `Nous vous contactons concernant le formulaire de collecte de données environnementales en attente. Merci de le compléter dans les meilleurs délais.`
}

export function RemindModal({
  campaignId,
  campaignName,
  suppliers,
  memberName,
  memberEmail,
  onSuccess,
  onClose,
}: Props) {
  const defaultSubject = `Relance — ${campaignName}`
  const defaultMessage = useMemo(() => getDefaultMessage(suppliers), [suppliers])

  const [subject, setSubject] = useState(defaultSubject)
  const [message, setMessage] = useState(defaultMessage)
  const [sending, setSending] = useState(false)

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) return
    setSending(true)
    apiSendReminders(campaignId, {
      supplierIds: suppliers.map(s => s.id),
      subject,
      message,
    })
      .then(({ sent, failed }) => {
        if (failed > 0) {
          toast.error(`${sent} relance(s) envoyée(s), ${failed} échec(s)`, { duration: Infinity })
        } else {
          toast.success(`${sent} relance(s) envoyée(s) avec succès`)
        }
        onSuccess()
      })
      .catch(() => {
        toast.error("Erreur lors de l'envoi des relances", { duration: Infinity })
      })
      .finally(() => setSending(false))
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Relancer {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Expéditeur — lecture seule */}
          <div className="rounded-md bg-muted p-3 text-sm">
            <span className="text-muted-foreground">De : </span>
            <span className="font-medium">{memberName}</span>
            {' '}
            <span className="text-muted-foreground">&lt;{memberEmail}&gt;</span>
            <p className="text-xs text-muted-foreground mt-1">
              L'email sera envoyé depuis l'adresse configurée de votre organisation.
            </p>
          </div>

          {/* Destinataires */}
          <div className="text-sm text-muted-foreground">
            À : {suppliers.map(s => s.name).join(', ')}
          </div>

          {/* Objet */}
          <div className="space-y-1">
            <Label htmlFor="remind-subject">Objet</Label>
            <Input
              id="remind-subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              disabled={sending}
            />
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label htmlFor="remind-message">Message</Label>
            <Textarea
              id="remind-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              disabled={sending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim()}
          >
            {sending ? 'Envoi…' : `Envoyer ${suppliers.length} relance${suppliers.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
